/**
 * DirectorClient — talks to the Cloud Run director service.
 *
 * Manages the full lifecycle of a director session:
 *   1. POST /direct/stream  — sends the user's prompt, receives a stream of
 *                              SSE events. Beats arrive one at a time as each
 *                              Uthana clip is generated, so playback starts
 *                              within seconds rather than waiting for all clips.
 *   2. POST /direct/continue — called automatically when the BeatPlayer's
 *                              queue drops low. Sends prompt + history +
 *                              elapsed seconds. The player never runs dry.
 *
 * ── SSE event types ────────────────────────────────────────────────────────
 *
 *   meta  — { session_id, default_idle } — emitted right after Gemini returns,
 *           before any Uthana generation. The client transitions to 'playing'
 *           here so the loading overlay dismisses quickly.
 *   beat  — Beat object — emitted as each Uthana clip finishes generating.
 *   done  — { session_id, warnings } — all beats delivered.
 *   error — { error } — unrecoverable error; client shows error state.
 *
 * ── Queue management ────────────────────────────────────────────────────────
 *
 * We keep the last MAX_HISTORY beats that have actually started playing
 * (reported via BeatPlayer's `onBeatStart` callback). This is what gets
 * sent to `/direct/continue` so the server can reason about what the
 * character just did.
 *
 * ── Failure handling ────────────────────────────────────────────────────────
 *
 * If the SSE stream errors, the client falls back to 'error' state.
 * If `/direct/continue` fails, we log a warning and set a 10s retry delay.
 * The BeatPlayer's fallback idle sustains the character in the meantime.
 */

import type { Beat, ContinueResponse } from './types';
import type { BeatPlayer } from './beat-player';

const MAX_HISTORY = 10;
const REFILL_RETRY_MS = 10_000;
const CONTINUE_TIMEOUT_MS = 120_000;

export type DirectorState =
  | { type: 'idle' }
  | { type: 'loading' }
  | { type: 'playing'; sessionId: string; prompt: string }
  | { type: 'error'; message: string };

export interface DirectorClientOpts {
  /** Base URL of the director server, e.g. http://localhost:8080 */
  serverUrl: string;
  beatPlayer: BeatPlayer;
  onStateChange?: (state: DirectorState) => void;
  /** Called when the server sends ambient audio URLs for the current scene.
   *  envUrl is always present; musicUrl is only set when the scene calls for diegetic music. */
  onAmbient?: (envUrl: string, musicUrl: string | null) => void;
  /** Called when the server has started a WorldLabs world generation job.
   *  The client should poll /world-status/:operationId until done.
   *  The second arg is the original prompt, forwarded so WorldLoader can
   *  label the cached entry correctly. */
  onWorldJob?: (operationId: string, prompt: string) => void;
  /** Called as soon as the SSE stream is connected (before the first event). */
  onStreamConnected?: () => void;
  /** Called when a beat has a pre-generated vocalization audio URL. */
  onVocalization?: (url: string, offsetSec: number) => void;
}

export class DirectorClient {
  private opts: DirectorClientOpts;
  private state: DirectorState = { type: 'idle' };

  private sessionId: string | null = null;
  private prompt: string | null = null;
  private voiceId: string | undefined = undefined;
  private gender = 'male';
  /**
   * LLM directing bias for the session. Captured from start(); included in
   * /direct/continue refill requests. Defaults to 'story' (current behaviour);
   * 'scene' tells the server to keep the LLM inside the prompt's ongoing
   * activity instead of advancing through chapters.
   */
  private biasMode: 'story' | 'scene' = 'story';
  private history: Beat[] = [];
  private sessionStartMs = 0;
  private refillPending = false;
  private refillRetryTimer: ReturnType<typeof setTimeout> | null = null;
  /**
   * Date.now() of the most recent successful /direct/continue refill (or 0
   * if none yet — falls back to sessionStartMs in `lastRefillSec`). The HUD
   * uses this to surface "time since last server response" so a stalled
   * refill is visible at a glance.
   */
  private lastRefillMs = 0;

  constructor(opts: DirectorClientOpts) {
    this.opts = opts;

    // Wire the BeatPlayer callbacks.
    opts.beatPlayer['callbacks'].onBeatStart = (beat: Beat) => {
      this.history.push(beat);
      if (this.history.length > MAX_HISTORY) {
        this.history = this.history.slice(-MAX_HISTORY);
      }
    };

    opts.beatPlayer['callbacks'].onQueueLow = () => {
      this.refill().catch((err) =>
        console.warn('[director] refill rejected:', err),
      );
    };

    if (opts.onVocalization) {
      opts.beatPlayer['callbacks'].onVocalization = opts.onVocalization;
    }
  }

  /** Start a new director session with a fresh prompt using SSE streaming.
   *  Pass skipWorldGen=true when a cached splat is already loaded so the
   *  server skips the WorldLabs generation call (saves credits).
   *  Pass voiceId to ensure per-beat vocalizations use the current character's voice.
   *  Pass biasMode='scene' to keep the LLM looping inside the prompt's
   *  ongoing activity instead of advancing through narrative chapters. */
  async start(
    prompt: string,
    skipWorldGen = false,
    voiceId?: string,
    gender = 'male',
    biasMode: 'story' | 'scene' = 'story',
  ): Promise<void> {
    if (this.refillRetryTimer !== null) {
      clearTimeout(this.refillRetryTimer);
      this.refillRetryTimer = null;
    }

    this.sessionId = null;
    this.prompt = prompt.trim();
    this.voiceId = voiceId;
    this.gender = gender;
    this.biasMode = biasMode;
    this.history = [];
    this.refillPending = false;
    this.lastRefillMs = 0;
    this.opts.beatPlayer.reset();

    this.setState({ type: 'loading' });

    try {
      const res = await fetch(`${this.opts.serverUrl}/direct/stream`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          prompt: this.prompt,
          ...(skipWorldGen ? { skip_world_gen: true } : {}),
          ...(voiceId ? { voice_id: voiceId } : {}),
          ...(gender ? { gender } : {}),
          // Send biasMode on the opening call too — the server forwards it
          // into buildSystemPrompt so the directing principles (rule 1, 9,
          // 10, few-shot examples) are themselves swapped for scene-mode.
          // Without this, the opening would still use story-biased rules
          // for ~30–40 s before the first /direct/continue arrives.
          bias_mode: this.biasMode,
        }),
      });

      if (!res.ok) {
        const text = await res.text().catch(() => res.statusText);
        throw new Error(`Server returned ${res.status}: ${text.slice(0, 200)}`);
      }

      if (!res.body) {
        throw new Error('Server returned no response body');
      }

      this.opts.onStreamConnected?.();

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let playerStarted = false;

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });

        // Split on double-newline (SSE event boundary)
        const parts = buffer.split('\n\n');
        buffer = parts.pop() ?? '';

        for (const chunk of parts) {
          if (!chunk.trim()) continue;

          // Parse SSE event
          let eventType = 'message';
          let dataLine = '';
          for (const line of chunk.split('\n')) {
            if (line.startsWith('event: ')) eventType = line.slice(7).trim();
            if (line.startsWith('data: ')) dataLine = line.slice(6);
          }

          if (!dataLine) continue;

          let payload: unknown;
          try {
            payload = JSON.parse(dataLine);
          } catch {
            console.warn('[director] SSE parse error for event:', eventType, dataLine.slice(0, 100));
            continue;
          }

          if (eventType === 'meta') {
            const meta = payload as { session_id: string; default_idle: string };
            this.sessionId = meta.session_id;
            this.sessionStartMs = Date.now();
            this.lastRefillMs = this.sessionStartMs;
            // Start the beat player now — it will idle until the first beat arrives
            if (!playerStarted) {
              playerStarted = true;
              this.opts.beatPlayer.start();
            }
            this.setState({
              type: 'playing',
              sessionId: meta.session_id,
              prompt: this.prompt!,
            });
            console.log('[director] meta received, session:', meta.session_id);
          } else if (eventType === 'beat') {
            const beat = this.resolveUrl(payload as Beat);
            this.opts.beatPlayer.enqueue([beat]);
          } else if (eventType === 'ambient') {
            const a = payload as { envUrl: string; musicUrl: string | null };
            if (a.envUrl) {
              console.log('[director] ambient env:', a.envUrl, a.musicUrl ? `| music: ${a.musicUrl}` : '');
              this.opts.onAmbient?.(a.envUrl, a.musicUrl ?? null);
            }
          } else if (eventType === 'world_job') {
            const w = payload as { operationId: string };
            if (w.operationId) {
              console.log('[director] world job:', w.operationId);
              this.opts.onWorldJob?.(w.operationId, this.prompt);
            }
          } else if (eventType === 'world_gen') {
            const w = payload as { status: string; reason?: string };
            console.warn('[director] world_gen:', w.status, w.reason ?? '');
          } else if (eventType === 'done') {
            const d = payload as { warnings?: string[] };
            if (d.warnings && d.warnings.length > 0) {
              console.warn('[director] server warnings:', d.warnings);
            }
          } else if (eventType === 'error') {
            throw new Error((payload as { error: string }).error);
          }
        }
      }
    } catch (err) {
      // Log the full error object so the browser console shows the stack and
      // any nested `cause` (e.g. "connection refused" from a failed fetch).
      console.error('[director] start failed — full error:', err);
      if (err instanceof Error && err.cause) {
        console.error('[director] caused by:', err.cause);
      }
      const msg = err instanceof Error ? err.message : String(err);
      this.setState({ type: 'error', message: msg });
    }
  }

  /** Stop the current session. The BeatPlayer continues with its fallback idle. */
  stop(): void {
    this.opts.beatPlayer.stop();
    this.sessionId = null;
    this.prompt = null;
    this.lastRefillMs = 0;
    this.setState({ type: 'idle' });
  }

  get currentState(): DirectorState {
    return this.state;
  }

  get secondsElapsed(): number {
    if (this.sessionStartMs === 0) return 0;
    return (Date.now() - this.sessionStartMs) / 1000;
  }

  /**
   * Snapshot of session-level telemetry for the debug HUD. Computed on
   * every read because the underlying numbers (history length, last refill
   * timestamp) change continuously — there's no value in caching.
   *
   *   - `sessionId`: short identifier from the server, or `null` before the
   *     SSE meta event arrives.
   *   - `beatsPlayed`: total beats that have hit the BeatPlayer onBeatStart
   *     callback this session (capped at MAX_HISTORY = 100 in practice;
   *     useful as a coarse counter rather than a precise total).
   *   - `paidBeats`: subset of beatsPlayed where Uthana actually generated
   *     motion (uthana_text or uthana_locomotion). Free fallbacks
   *     (face_only) and library beats don't count. Mirrors the
   *     DIRECTOR_BEATS_LIMIT bookkeeping on the server.
   *   - `lastRefillSec`: seconds since the most recent successful server
   *     response (initial /direct/stream meta, or any /direct/continue
   *     batch). 0 before any response arrives. Lets the HUD flash a stall
   *     indicator if the server has gone quiet.
   */
  get sessionInfo(): {
    sessionId: string | null;
    beatsPlayed: number;
    paidBeats: number;
    lastRefillSec: number;
  } {
    const paidBeats = this.history.filter(
      (b) => b.source === 'uthana_text' || b.source === 'uthana_locomotion',
    ).length;
    const lastRefillSec =
      this.lastRefillMs === 0 ? 0 : (Date.now() - this.lastRefillMs) / 1000;
    return {
      sessionId: this.sessionId,
      beatsPlayed: this.history.length,
      paidBeats,
      lastRefillSec,
    };
  }

  // ── Private ──────────────────────────────────────────────────────────────

  private async refill(): Promise<void> {
    if (this.refillPending) return;
    if (!this.sessionId || !this.prompt) return;
    if (this.state.type !== 'playing') return;

    this.refillPending = true;
    try {
      const res = await fetch(`${this.opts.serverUrl}/direct/continue`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          session_id: this.sessionId,
          prompt: this.prompt,
          history: this.history,
          seconds_elapsed: this.secondsElapsed,
          ...(this.voiceId ? { voice_id: this.voiceId } : {}),
          ...(this.gender ? { gender: this.gender } : {}),
          bias_mode: this.biasMode,
        }),
        signal: AbortSignal.timeout(CONTINUE_TIMEOUT_MS),
      });

      if (!res.ok) {
        throw new Error(`Continue returned ${res.status}`);
      }

      const data: ContinueResponse = await res.json();
      console.log(`[director] refill: +${data.beats.length} beats`);
      this.lastRefillMs = Date.now();
      this.opts.beatPlayer.enqueue(data.beats.map((b) => this.resolveUrl(b)));
    } catch (err) {
      console.warn('[director] refill failed:', err, `— retrying in ${REFILL_RETRY_MS / 1000}s`);
      this.refillRetryTimer = setTimeout(() => {
        this.refillPending = false;
        this.refill().catch(() => {});
      }, REFILL_RETRY_MS);
      return;
    }
    this.refillPending = false;
    // If the queue is still at or below the threshold after the refill
    // (e.g. the server returned fewer beats than the threshold, or the
    // player consumed them all while we were waiting), kick off another
    // refill immediately. Without this, notifyQueueDepth only fires when
    // a beat is *consumed*, so a queue that hits 0 while a refill is
    // in-flight never re-triggers onQueueLow after refillPending clears,
    // and the performance stalls permanently.
    const threshold = this.opts.beatPlayer['callbacks'].queueLowThreshold ?? 1;
    if (this.opts.beatPlayer.queueDepth <= threshold) {
      this.refill().catch((err) =>
        console.warn('[director] follow-up refill rejected:', err),
      );
    }
  }

  private setState(s: DirectorState): void {
    this.state = s;
    this.opts.onStateChange?.(s);
  }

  /**
   * Uthana cache URLs come back as root-relative paths (/uthana-cache/xxx.glb).
   * Prefix them with the director server origin so the browser fetches from
   * Express, not from the Vite dev server.
   *
   * IMPORTANT: this must always process BOTH the body URL and any hold URLs.
   * The earlier version early-returned when the body URL was a uthana-cache
   * path, leaving the holds with relative URLs — which the browser would then
   * fetch against Vite's origin, getting the SPA fallback HTML, breaking the
   * GLTFLoader with "Unexpected token '<'". Don't reintroduce an early return.
   */
  private resolveUrl(beat: import('./types').Beat): import('./types').Beat {
    // Any server-relative URL (starting with /) must be prefixed with the
    // director server origin — otherwise the browser resolves it against the
    // Vite dev server and gets an HTML 404, breaking GLTFLoader.
    const fixUrl = (url: string) =>
      url.startsWith('/') ? `${this.opts.serverUrl}${url}` : url;
    const fixedUrl = fixUrl(beat.url);
    const fixedHolds = beat.holds?.map((h) => ({ ...h, url: fixUrl(h.url) }));
    return {
      ...beat,
      url: fixedUrl,
      ...(fixedHolds ? { holds: fixedHolds } : {}),
    };
  }
}
